import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Link, useNavigate } from 'react-router-dom';
import {
    GraduationCap, ArrowLeft, Target, Coins, CheckCircle,
    CaretRight, Calendar, Users, Trophy, Star
} from '@phosphor-icons/react';

export default function StudentClassroomPage() {
    const { user, api } = useAuth();
    const navigate = useNavigate();
    const [classrooms, setClassrooms] = useState([]);
    const [selectedClassroom, setSelectedClassroom] = useState(null);
    const [classroomData, setClassroomData] = useState(null);
    const [loading, setLoading] = useState(true);

    const loadClassrooms = useCallback(async () => {
        try {
            const res = await api().get('/educator/my-classrooms');
            const data = res.data || [];
            setClassrooms(data);
            // Auto-select if only one classroom
            if (data.length === 1) {
                setSelectedClassroom(data[0]);
            }
        } catch (error) {
            console.error('Failed to load classrooms:', error);
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        loadClassrooms();
    }, [loadClassrooms]);

    const loadClassroomView = useCallback(async (classroomId) => {
        try {
            const res = await api().get(`/educator/classroom/${classroomId}/student-view`);
            setClassroomData(res.data);
        } catch (error) {
            toast.error('Failed to load classroom');
        }
    }, [api]);

    useEffect(() => {
        if (selectedClassroom) {
            loadClassroomView(selectedClassroom.id);
        }
    }, [selectedClassroom, loadClassroomView]);

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    // No classrooms
    if (classrooms.length === 0) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="brutalist-card p-8 max-w-md text-center"
                >
                    <GraduationCap weight="duotone" size={56} className="text-[#94a3b8] mx-auto mb-4" />
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-2">No Classroom Yet</h2>
                    <p className="text-[#475569] mb-6">
                        Ask your teacher or parent for a class code to join a classroom.
                    </p>
                    <div className="flex gap-3 justify-center">
                        <Link to="/join-classroom" className="brutalist-btn bg-[#22c55e] text-white" data-testid="join-classroom-link">
                            Enter Class Code
                        </Link>
                        <Link to="/dashboard" className="brutalist-btn bg-white text-[#1e293b]">
                            Back
                        </Link>
                    </div>
                </motion.div>
            </div>
        );
    }

    // Multiple classrooms — selection screen
    if (!selectedClassroom || (classrooms.length > 1 && !classroomData)) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
                <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
                    <Link to="/dashboard" className="flex items-center gap-2 text-[#475569] hover:text-[#1e293b] mb-4 font-semibold">
                        <ArrowLeft weight="bold" size={20} /> Back to Dashboard
                    </Link>
                    <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b] mb-2">
                        My Classrooms
                    </h1>
                    <p className="text-[#475569] mb-6">Choose a classroom to see your assigned missions.</p>

                    <div className="grid sm:grid-cols-2 gap-4">
                        {classrooms.map(c => (
                            <motion.div
                                key={c.id}
                                whileHover={{ y: -4, x: -4 }}
                                className="brutalist-card p-5 cursor-pointer"
                                onClick={() => setSelectedClassroom(c)}
                                data-testid={`select-classroom-${c.id}`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <div>
                                        <h3 className="font-fredoka text-lg font-bold text-[#1e293b]">{c.name}</h3>
                                        {c.school_name && (
                                            <p className="text-sm text-[#475569]">{c.school_name}</p>
                                        )}
                                    </div>
                                    <CaretRight weight="bold" className="text-[#94a3b8]" size={20} />
                                </div>
                                <div className="flex items-center gap-4 text-sm text-[#475569]">
                                    <span className="flex items-center gap-1">
                                        <Users size={16} /> {c.student_count} students
                                    </span>
                                    <span className="flex items-center gap-1">
                                        <Target size={16} /> {c.assignment_count} assigned
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    // Single classroom view with assigned missions
    if (!classroomData) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const { classroom, assigned_missions, classmates, my_progress } = classroomData;
    const pendingMissions = assigned_missions.filter(m => !m.completed);
    const completedMissions = assigned_missions.filter(m => m.completed);

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            <div className="max-w-4xl mx-auto px-4 py-6 pb-24">
                {/* Header */}
                <button
                    onClick={() => {
                        if (classrooms.length > 1) {
                            setSelectedClassroom(null);
                            setClassroomData(null);
                        } else {
                            navigate('/dashboard');
                        }
                    }}
                    className="flex items-center gap-2 text-[#475569] hover:text-[#1e293b] mb-4 font-semibold"
                    data-testid="back-btn"
                >
                    <ArrowLeft weight="bold" size={20} />
                    {classrooms.length > 1 ? 'My Classrooms' : 'Back to Dashboard'}
                </button>

                <div className="mb-6">
                    <div className="flex items-center gap-3 mb-1">
                        <div className="w-10 h-10 rounded-full bg-[#22c55e] border-3 border-[#1e293b] flex items-center justify-center">
                            <GraduationCap weight="fill" className="text-white" size={20} />
                        </div>
                        <div>
                            <h1 className="font-fredoka text-2xl md:text-3xl font-bold text-[#1e293b]">
                                {classroom.name}
                            </h1>
                            {classroom.school_name && (
                                <p className="text-sm text-[#475569]">{classroom.school_name}</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Progress Stats */}
                <div className="grid grid-cols-3 gap-3 mb-6">
                    <div className="brutalist-card p-4 text-center">
                        <CheckCircle weight="duotone" size={24} className="text-[#22c55e] mx-auto" />
                        <p className="font-fredoka text-2xl font-bold text-[#1e293b] mt-1">
                            {my_progress.completed_count}/{my_progress.total_assigned}
                        </p>
                        <p className="text-xs text-[#475569]">Completed</p>
                    </div>
                    <div className="brutalist-card p-4 text-center">
                        <Coins weight="duotone" size={24} className="text-[#f59e0b] mx-auto" />
                        <p className="font-fredoka text-2xl font-bold text-[#1e293b] mt-1">
                            {assigned_missions.filter(m => m.completed).reduce((sum, m) => sum + m.xp_reward, 0)}
                        </p>
                        <p className="text-xs text-[#475569]">Tokens Earned</p>
                    </div>
                    <div className="brutalist-card p-4 text-center">
                        <Users weight="duotone" size={24} className="text-[#38bdf8] mx-auto" />
                        <p className="font-fredoka text-2xl font-bold text-[#1e293b] mt-1">
                            {classmates.length + 1}
                        </p>
                        <p className="text-xs text-[#475569]">In Class</p>
                    </div>
                </div>

                {/* Progress Bar */}
                {my_progress.total_assigned > 0 && (
                    <div className="brutalist-card p-4 mb-6">
                        <div className="flex justify-between items-center mb-2">
                            <span className="font-fredoka font-semibold text-[#1e293b]">Your Progress</span>
                            <span className="text-sm font-bold text-[#22c55e]">
                                {Math.round((my_progress.completed_count / my_progress.total_assigned) * 100)}%
                            </span>
                        </div>
                        <div className="w-full h-3 bg-[#e2e8f0] rounded-full border-2 border-[#1e293b]">
                            <div
                                className="h-full bg-[#22c55e] rounded-full transition-all duration-500"
                                style={{ width: `${(my_progress.completed_count / my_progress.total_assigned) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {/* Pending Missions */}
                {pendingMissions.length > 0 && (
                    <section className="mb-6">
                        <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-3 flex items-center gap-2">
                            <Target weight="duotone" size={22} className="text-[#f59e0b]" />
                            To Do ({pendingMissions.length})
                        </h2>
                        <div className="space-y-3">
                            {pendingMissions.map(m => (
                                <Link
                                    key={m.id}
                                    to={`/missions/${m.id}`}
                                    className="block"
                                    data-testid={`mission-card-${m.id}`}
                                >
                                    <motion.div
                                        whileHover={{ y: -3, x: -3 }}
                                        className="brutalist-card p-4 flex items-center gap-4"
                                    >
                                        {m.image_url && (
                                            <img
                                                src={m.image_url}
                                                alt={m.title}
                                                className="w-16 h-16 rounded-xl border-2 border-[#1e293b] object-cover shrink-0"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <h3 className="font-fredoka font-bold text-[#1e293b] truncate">{m.title}</h3>
                                            <p className="text-sm text-[#475569] line-clamp-1">{m.description}</p>
                                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                                <span className="text-xs bg-[#f59e0b]/10 text-[#f59e0b] px-2 py-0.5 rounded-full font-bold">
                                                    +{m.xp_reward} Tokens
                                                </span>
                                                <span className="text-xs bg-[#e2e8f0] px-2 py-0.5 rounded-full capitalize">{m.difficulty}</span>
                                                {m.due_date && (
                                                    <span className="text-xs text-[#ef4444] font-semibold flex items-center gap-1">
                                                        <Calendar size={12} /> Due {new Date(m.due_date).toLocaleDateString()}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <CaretRight weight="bold" size={20} className="text-[#94a3b8] shrink-0" />
                                    </motion.div>
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* Completed Missions */}
                {completedMissions.length > 0 && (
                    <section className="mb-6">
                        <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-3 flex items-center gap-2">
                            <CheckCircle weight="duotone" size={22} className="text-[#22c55e]" />
                            Completed ({completedMissions.length})
                        </h2>
                        <div className="space-y-3">
                            {completedMissions.map(m => (
                                <div
                                    key={m.id}
                                    className="brutalist-card p-4 flex items-center gap-4 opacity-70"
                                    data-testid={`completed-mission-${m.id}`}
                                >
                                    {m.image_url && (
                                        <img
                                            src={m.image_url}
                                            alt={m.title}
                                            className="w-16 h-16 rounded-xl border-2 border-[#1e293b] object-cover shrink-0 grayscale"
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-fredoka font-bold text-[#1e293b] truncate">{m.title}</h3>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs bg-[#22c55e]/10 text-[#22c55e] px-2 py-0.5 rounded-full font-bold">
                                                +{m.xp_reward} Tokens earned
                                            </span>
                                        </div>
                                    </div>
                                    <CheckCircle weight="fill" size={24} className="text-[#22c55e] shrink-0" />
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Empty state */}
                {assigned_missions.length === 0 && (
                    <div className="brutalist-card p-8 text-center">
                        <Target weight="duotone" size={48} className="text-[#94a3b8] mx-auto mb-3" />
                        <h3 className="font-fredoka text-lg font-bold text-[#1e293b] mb-1">No Missions Yet</h3>
                        <p className="text-[#475569]">Your teacher hasn't assigned any missions yet. Check back soon!</p>
                    </div>
                )}

                {/* Classmates */}
                {classmates.length > 0 && (
                    <section>
                        <h2 className="font-fredoka text-xl font-bold text-[#1e293b] mb-3 flex items-center gap-2">
                            <Users weight="duotone" size={22} className="text-[#38bdf8]" />
                            Classmates
                        </h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                            {classmates.map(c => (
                                <div key={c.id} className="brutalist-card p-3 flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-[#38bdf8]/10 border-2 border-[#1e293b] flex items-center justify-center">
                                        <Star weight="fill" size={16} className="text-[#38bdf8]" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="font-fredoka font-bold text-[#1e293b] text-sm truncate">{c.name}</p>
                                        <p className="text-xs text-[#475569]">{c.xp || 0} Tokens</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}
